import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useFilterManager } from "web/react/components/feeds-filters/hooks/use-filter-manager";
import { FILTERS_OVERLAY_NAME } from "web/react/components/feeds-filters/hooks/use-overlay-handler";
import { FILTER_SECTION } from "web/react/components/filters-emo/filter-utils";
import { useDomViewport } from "web/react/hooks/use-dom-viewport/use-dom-viewport";
import { ReduxStoreState } from "web/redux/store";
import analytics from "web/script/analytics/analytics";
import history from "web/script/utils/history";
import {
    OptionFilterOption,
    UniversalFilterOption,
    UniversalFiltersData,
} from "web/types/universal-filters";
import { getPopularDesignerProfilePills } from "./get-popular-designer-profile-pills";
import { getPopularDesignerTaxonomyPills } from "./get-popular-designer-taxonomy-pills";
import { UsePopularDesignerPills } from "./types";

export function usePopularDesignerPills(filters: UniversalFiltersData): UsePopularDesignerPills {
    const [hasAppliedFilter, setHasAppliedFilter] = useState(false);

    const isFetching = useSelector((state: ReduxStoreState) => state.feed.ui.isFetching);
    const { displayedFilters, onClick, isLoading, applyMultiple, applyState, pendingFilterInfo } =
        useFilterManager(filters);
    const { isMobileViewport, isTabletViewport } = useDomViewport();

    function onPillClick(filter: UniversalFilterOption): void {
        analytics.event("filter_pill", "click", String(filter.value));

        // Needed on mobile so the filters work correctly
        // taken from the filter-container-emo logic
        if (isMobileViewport || isTabletViewport) {
            history.popOverlay(FILTERS_OVERLAY_NAME);
        }

        // If we are trying to apply a category filter (from Designer Profile feed pill)
        // we first need to apply a product type filter and then the category one
        // otherwise the filters won't work as expected
        const availableProductTypeFilters = displayedFilters.filter_section_list.find(
            (section) => section.id === FILTER_SECTION.categoryFilter
        )?.option_groups[0].options as OptionFilterOption[] | undefined;
        const selectedProductTypeFilter = availableProductTypeFilters?.find(
            (filter) => filter.checked
        );

        if (
            filter.name === "category" &&
            availableProductTypeFilters &&
            !selectedProductTypeFilter
        ) {
            // 1. Find the ancestor of the category filter we are trying to apply
            const productTypeFilter = availableProductTypeFilters.find((ptf) =>
                ptf.children?.find((cf) => cf.value === filter.value)
            );
            // 2. Apply the product type and the category filters to the pending filters
            // afterwards a hook will trigger the state update
            productTypeFilter
                ? applyMultiple([productTypeFilter, filter])
                : onClick(filter, false, true);
        } else {
            onClick(filter, false, true);
        }

        setHasAppliedFilter(true);
    }

    useEffect(() => {
        if (isLoading || !hasAppliedFilter) {
            return;
        }

        if (pendingFilterInfo.hasPending()) {
            applyState();
        }

        setHasAppliedFilter(false);
    }, [
        isLoading,
        hasAppliedFilter,
        isMobileViewport,
        isTabletViewport,
        pendingFilterInfo,
        applyState,
    ]);

    const popularDesignerTaxonomyPills = getPopularDesignerTaxonomyPills(displayedFilters);
    const popularDesignerProfilePills = getPopularDesignerProfilePills(displayedFilters);

    return {
        pills: popularDesignerTaxonomyPills.pills || popularDesignerProfilePills.pills,
        designer: popularDesignerTaxonomyPills.designer || popularDesignerProfilePills.designer,
        isLoading: isLoading || isFetching,
        onPillClick,
    };
}
